import { useState, useMemo } from 'react';

interface AgreementTagsMap {
  [agreementId: string]: string[]; // agreementId -> array of coreEnumOptionIds
}

interface UseAgreementTagFilteringReturn {
  selectedTagIds: Set<string>;
  setSelectedTagIds: (tags: Set<string>) => void;
  agreementTagsMap: AgreementTagsMap;
  availableTagIds: Set<string>;
  matchesFilters: (agreementId: string) => boolean; // Returns true if agreement matches selected filters
  filteredAgreementIds: Set<string>; // Agreements that match the selected filters
}

interface AgreementWithTags {
  id: string;
  tags?: Array<{ id: string; name: string }>;
}

/**
 * Hook to manage tag filtering for rebate agreements
 * Uses OR logic: an agreement must have ANY selected tag to match
 *
 * Tags are provided directly from the forecast data (no lazy loading needed)
 */
export function useAgreementTagFiltering(
  agreements: AgreementWithTags[] | undefined,
): UseAgreementTagFilteringReturn {
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());

  // Build agreementTagsMap directly from agreements array
  const agreementTagsMap = useMemo(() => {
    const tagsMap: AgreementTagsMap = {};
    if (!agreements) return tagsMap;

    agreements.forEach((agreement) => {
      tagsMap[agreement.id] = agreement.tags?.map((tag) => tag.id) || [];
    });

    return tagsMap;
  }, [agreements]);

  // Collect all unique tag IDs available across all agreements
  const availableTagIds = useMemo(() => {
    const tags = new Set<string>();
    Object.values(agreementTagsMap).forEach((agreementTags) => {
      agreementTags.forEach((tagId) => tags.add(tagId));
    });
    return tags;
  }, [agreementTagsMap]);

  // Helper: Check if an agreement matches the selected filters (OR logic)
  const matchesFilters = useMemo(() => {
    return (agreementId: string): boolean => {
      if (selectedTagIds.size === 0) {
        return true; // No filter = all agreements match
      }

      const agreementTags = agreementTagsMap[agreementId] || [];
      // Check if agreement has ANY selected tag
      return Array.from(selectedTagIds).some((tagId) =>
        agreementTags.includes(tagId),
      );
    };
  }, [agreementTagsMap, selectedTagIds]);

  // Get all agreement IDs that match the current filters
  const filteredAgreementIds = useMemo(() => {
    if (!agreements) return new Set<string>();

    if (selectedTagIds.size === 0) {
      // No tags selected = all agreements match
      return new Set(agreements.map((a) => a.id));
    }

    // Filter agreements that have ANY selected tag
    const filtered = new Set<string>();
    agreements.forEach((agreement) => {
      if (matchesFilters(agreement.id)) {
        filtered.add(agreement.id);
      }
    });

    return filtered;
  }, [agreements, matchesFilters, selectedTagIds]);

  return {
    selectedTagIds,
    setSelectedTagIds,
    agreementTagsMap,
    availableTagIds,
    matchesFilters,
    filteredAgreementIds,
  };
}
